import { Alert, Button, Card, Grid, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { PropertyListItem } from "../../common/components/property-list-item";
import DashboardLayout from "../../common/layouts/dashboard";
import { RootState } from "../../core/store/store";
import PaykickstartEmbed from "./paykickstart-embed";
import BillingPlan from "./billing-plan";
import Subscriptions from "./subscriptions";
import Paddle from "./paddle";
import getBillingProcessor, { isSubscriptionCurrentlyActive } from "./get-billing-processor";

const BILLING_EMAIL = "support@wordform.ai";

const Billing = () => {
  const authState = useSelector((state: RootState) => state.auth);
  const billingProcessor = getBillingProcessor(authState.subscriptions, authState.user?.processor);
  const activeSubNum = authState.subscriptions?.subscriptions
    ? authState.subscriptions?.subscriptions.filter((s) => isSubscriptionCurrentlyActive(s)).length
    : 0;
  const hasLoadedSubscriptions = authState.subscriptions !== undefined;
  const hasNoActiveSubscription = hasLoadedSubscriptions && activeSubNum === 0;
  const latestInactiveSubscription =
    authState.subscriptions?.subscriptions && authState.subscriptions.subscriptions.length > 0
      ? [...authState.subscriptions.subscriptions]
          .filter((subscription) => subscription.status !== "active" && subscription.status !== "trial")
          .sort((a, b) => {
            const aDate = new Date(a.endDate || a.rebillDate || a.startDate).getTime();
            const bDate = new Date(b.endDate || b.rebillDate || b.startDate).getTime();

            return bDate - aDate;
          })[0]
      : null;

  useEffect(() => {
    document.title = "Wordfrom AI - Billing";
  }, []);

  // Paddle also supports reactivation and one-time credit purchases, so keep its
  // billing screen available after a legacy subscription expires.
  if (billingProcessor === "paddle") {
    return <Paddle />;
  }
  return (
    <DashboardLayout active={"billing"} name="Billing">
      <Alert color="info" severity="info" sx={{ mt: 3, mb: 3 }}>
        To cancel your account, please send an email to <b>{BILLING_EMAIL}</b> with your cancellation request.
      </Alert>
      {activeSubNum === 0 && (
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2} alignItems="flex-start">
            <Alert severity="warning" sx={{ width: "100%" }}>
              You do not have an active Wordform subscription. Your latest subscription status is still shown below so
              you can see when access ended.
            </Alert>
            {latestInactiveSubscription && (
              <Typography variant="body2" color="text.secondary">
                Most recent status: <b>{latestInactiveSubscription.status}</b>
                {latestInactiveSubscription.endDate &&
                  `, ended on ${new Date(latestInactiveSubscription.endDate).toLocaleDateString()}`}
                .
              </Typography>
            )}
            <Button href="https://wordform.ai/evergreen/" target="_blank" rel="noreferrer" variant="contained">
              Reactivate Subscription
            </Button>
            <Typography variant="body2" color="text.secondary">
              Need help? Contact <b>{BILLING_EMAIL}</b>.
            </Typography>
          </Stack>
        </Card>
      )}
      <Stack spacing={3}>
        <Card>
          <Grid container>
            <Grid xs={12} md={3}>
              <PropertyListItem
                align={"vertical"}
                component="div"
                label="Plan Selected"
                value={authState.plan?.selected?.name || "No Plan Selected"}
              />
            </Grid>
            <Grid xs={12} md={3}>
              <PropertyListItem
                align={"vertical"}
                component="div"
                label="Articles Used"
                value={authState.credits?.used + " articles"}
              />
            </Grid>
            <Grid xs={12} md={3}>
              <PropertyListItem
                align={"vertical"}
                component="div"
                label="Articles Available"
                value={authState.credits?.available + " articles"}
              />
            </Grid>
            <Grid xs={12} md={3}>
              {
                <PropertyListItem
                  align={"vertical"}
                  component="div"
                  label="Active Subscriptions"
                  value={activeSubNum + " Subscriptions"}
                />
              }
            </Grid>
          </Grid>
        </Card>
        {!hasNoActiveSubscription && billingProcessor === "paykickstart" && <PaykickstartEmbed />}
        {!hasNoActiveSubscription && billingProcessor !== "paykickstart" && <BillingPlan />}
        <Subscriptions />
      </Stack>

    </DashboardLayout>
  );
};

export default Billing;
